import { DimensionEnums } from "models/enums";
import { FormFieldType } from "models/enums/form";
import {
  DimensionInputSelectorExtraProps,
  DimensionSelectorExtraProps,
  FormSectionObject,
  GenericFormField,
  GenericFormSection,
} from "models/propTypes/genericFormProps";
import { useSelector } from "react-redux";
import { AppState } from "store";

function useGetTotalFields<T extends Record<string, any>>() {
  const dimensions = useSelector((store: AppState) => {
    return store.common.dimensions;
  });

  const getDimensionsByType = (type: DimensionEnums.DimensionCategory) => {
    switch (type) {
      case DimensionEnums.DimensionCategory.IncomingInvoice:
        return dimensions.incomingInvoiceDimensions ?? [];
      case DimensionEnums.DimensionCategory.Receipt:
        return dimensions.receiptDimensions ?? [];
      case DimensionEnums.DimensionCategory.Invoice:
        return dimensions.invoiceDimensions ?? [];
      case DimensionEnums.DimensionCategory.CashReconciliation:
        return dimensions.cashReconciliationDimensions ?? [];
      case DimensionEnums.DimensionCategory.All:
        return dimensions.allDimensions ?? [];
      case DimensionEnums.DimensionCategory.MissingDocumentOther:
        return (dimensions.invoiceDimensions ?? []).concat(dimensions.receiptDimensions ?? []);
      default:
        return [];
    }
  };

  const getTotal = (item: GenericFormField<T> | GenericFormSection<T>, name?: string): number => {
    if (item.innerFields !== undefined) {
      const innerObj = Array.isArray(item.innerFields)
        ? (item.innerFields as any).map((innerField: GenericFormSection<T>) => getTotal(innerField))
        : Object.entries(item.innerFields).map(([key, innerField]) => innerField && getTotal(innerField, key));

      const total: number = innerObj
        .map((dim: FormSectionObject) => dim.total)
        .reduce((tot: number, val: number) => tot + val, 0);
      return total;
    } else {
      const field = item as GenericFormField<T>;

      switch (item.type) {
        case FormFieldType.DIMENSIONSELECTOR:
          const selectorExtraProps = field.extraProps as DimensionSelectorExtraProps;
          return getDimensionsByType(selectorExtraProps.type).length;
        case FormFieldType.DIMENSIONINPUTSELECTOR:
          const inputSelectorExtraProps = field.extraProps as DimensionInputSelectorExtraProps;
          return getDimensionsByType(inputSelectorExtraProps.type).length;
        default:
          return 1;
      }
    }
  };

  return {
    getTotal,
  };
}

export default useGetTotalFields;
